import React, { useCallback } from 'react';
import { useSelectByTypeList } from '../../hooks/selectByTypeHook';
import TableFooterWrapper from '../../modules/table-footer-wrapper/table-footer-wrapper';
import TableWrapper from '../../modules/table-wrapper/table-wrapper';
import {
  ORDER_DETAILED_STATUS,
  ORDER_STATUS,
  SORT_DIRECTION,
} from '../../types';
import currencyFormat from '../../utils/currencyFormat';
import { getOrderStatusBadge } from '../../utils/getStatusBadge';
import OrderDetailedAction from './order-detailed-action/order-detailed-action';
import OrderDetailedFilters from './order-detailed-filters/order-detailed-filters';
import OrderDetailedHeader from './order-detailed-header/order-detailed-header';
import OrderDetailedList from './order-detailed-list/order-detailed-list';
import OrderDetailedSorts from './order-detailed-sorts/order-detailed-sorts';
import { OrderDetailedItemProps } from './types';
import './order-detailed.scss';

interface OrderDetailedProps {
  isInitiator: boolean;
  data: OrderDetailedItemProps[];
  inn: string;
  name: string;
  refId: string;
  date: string;
  time: string;
  status: ORDER_STATUS;
  onSubmit: (ids: number[], itemsIds: number[]) => void;
  statuses: { value: string; label: string }[];
  id: number;
  onQueryChange: (val: string) => void;
  onStatusChange: (val: string) => void;
  goDetailedPosition: (id: number, itemId: number, attached?: boolean) => void;
  directions: {
    positions: SORT_DIRECTION;
    count: SORT_DIRECTION;
    sum: SORT_DIRECTION;
  };
  onToggleSort: (
    name: 'positions' | 'sum' | 'count',
    direction: SORT_DIRECTION
  ) => void;
  onClose: () => void;
  balance: number;
}

const OrderDetailed: React.FC<OrderDetailedProps> = ({
  data,
  onClose,
  onStatusChange,
  isInitiator,
  onToggleSort,
  directions,
  goDetailedPosition,
  onQueryChange,
  statuses,
  id,
  onSubmit,
  time,
  status,
  date,
  inn,
  name,
  balance,
  refId,
}) => {
  const {
    list,
    addItem,
    clear,
    currentStatus,
  } = useSelectByTypeList<ORDER_DETAILED_STATUS>(
    isInitiator ? 'UCP_ISSUING_REQUIRED' : 'UCP_PAYMENT_REQUIRED'
  );

  const onListItemClick = (clickId: number, type: ORDER_DETAILED_STATUS) => {
    addItem(clickId, type);
  };

  const onStatusChangeHandler = (val: string) => {
    clear();
    onStatusChange(val);
  };

  const positionNum = useCallback(() => {
    let position = 0;
    list.forEach((item) => {
      const dataItem = data.find((dItem) => dItem.id === item);
      if (dataItem) {
        position += dataItem.positionAmount;
      }
    });
    return position;
  }, [list]);

  const totalNum = () => {
    let total = 0;
    list.forEach((item) => {
      const dataItem = data.find((dItem) => dItem.id === item);
      if (dataItem) {
        total += dataItem.positionCount;
      }
    });
    return total;
  };

  const totalSum = () => {
    let total = 0;
    list.forEach((item) => {
      const dataItem = data.find((dItem) => dItem.id === item);
      if (dataItem) {
        total += dataItem.positionSum;
      }
    });
    return total;
  };

  const checkBalanceError = () =>
    balance < totalSum() && currentStatus === 'UCP_PAYMENT_REQUIRED';

  return (
    <TableWrapper>
      <div className="order">
        <div className="order__header">
          <OrderDetailedHeader
            onClose={onClose}
            inn={inn}
            id={refId}
            title={name}
            date={date}
            time={time}
            status={getOrderStatusBadge(status)}
          />
          <OrderDetailedFilters
            onStatusChange={onStatusChangeHandler}
            onQueryChange={onQueryChange}
            statuses={statuses}
          />
          <OrderDetailedSorts
            onItemToggle={onToggleSort}
            directions={directions}
          />
          <div className="order__body">
            <OrderDetailedList
              isInitiator={isInitiator}
              orderId={id}
              goDetailedPosition={goDetailedPosition}
              selected={list}
              onItemClick={onListItemClick}
              data={data}
            />
          </div>
          {list.length ? (
            <OrderDetailedAction
              isError={checkBalanceError()}
              errorText="Недостаточно финансовых токенов"
              isInitiator={isInitiator}
              onSubmit={() => {
                const ids = data
                  .filter(
                    (item) =>
                      item.issuingInfoId !== null && list.includes(item.id)
                  )
                  .map((item) => item.issuingInfoId);
                if (currentStatus === 'UCP_PAYMENT_REQUIRED') {
                  onSubmit(ids, list);
                } else if (currentStatus === 'UCP_ISSUING_REQUIRED') {
                  onSubmit(list, list);
                }
              }}
              count={list.length}
              total={totalNum()}
              picked={positionNum()}
              sum={currencyFormat(totalSum(), false)}
            />
          ) : (
            <TableFooterWrapper blank />
          )}
        </div>
      </div>
    </TableWrapper>
  );
};

export default OrderDetailed;
